import { LoaderIcon, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { Button } from '../ui/button';

export interface LoadingStateProps {
  colSpan: number;
}

export const DefaultTableLoadingState: React.FC<LoadingStateProps> = ({ colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="mt-3.5 w-full px-0 py-3">
      <div className="sticky left-1/2 flex w-fit -translate-x-1/2 content-center justify-center px-3">
        <LoaderIcon className="h-4 w-4 animate-spin text-foreground opacity-50" />
      </div>
    </td>
  </tr>
);

export interface ErrorStateProps {
  colSpan: number;
  error: string;
  retryLoad: () => void;
}

export interface EmptyStateProps {
  colSpan: number;
}

interface CenteredTableStateContentProps {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'center';
}

export const StickyTableStateContent: React.FC<CenteredTableStateContentProps> = ({
  children,
  className,
  align = 'start',
}) => (
  <div
    className={cn(
      'sticky flex w-fit flex-col',
      align === 'center' ? 'left-1/2 -translate-x-1/2 items-center' : 'left-0 items-start',
      className
    )}
  >
    {children}
  </div>
);

export const DefaultTableErrorState: React.FC<ErrorStateProps> = ({
  colSpan,
  error,
  retryLoad,
}) => {
  const { t } = useTranslation('common');

  return (
    <tr>
      <td colSpan={colSpan} className="h-16 w-full px-0">
        <StickyTableStateContent align="center" className="gap-2 px-12 py-4">
          <Button type="button" variant="outline" size="sm" onClick={() => retryLoad()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('retry')}
          </Button>
          <span className="text-xs text-destructive">{error}</span>
        </StickyTableStateContent>
      </td>
    </tr>
  );
};

export const DefaultTableEmptyState: React.FC<EmptyStateProps> = ({ colSpan }) => {
  const { t } = useTranslation('common');

  return (
    <tr>
      <td colSpan={colSpan} className="w-full px-0">
        <StickyTableStateContent className="px-8 py-8 text-left">
          <div className="text-sm font-medium text-muted-foreground">{t('noResultsFound')}</div>
        </StickyTableStateContent>
      </td>
    </tr>
  );
};
